import { basicAuth, bearerAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

const keywords = "spl siem logs search index enterprise cloud monitoring";
const publicEnvVars = ({ baseUrl }: Record<string, string>) => ({
  SPLUNK_URL: baseUrl || "",
});

export const splunk = defineProduct({
  name: "Splunk",
  tagLine: "Security and observability",
  logo,
  tags: ["featured", "it", "security", "observability", "data"],
  variants: [
    productVariant({
      identifier: "splunk",
      authLabel: "Authentication token",
      keywords,
      protocol: "http",
      defaultUrlPattern: ({ baseUrl }) => exactUrlToRegex(baseUrl),
      auth: (app) => bearerAuth(app.secret.token),
      steps: [
        {
          title: "Get a Splunk authentication token",
          items: [
            {
              kind: "markdown",
              content:
                "In Splunk, navigate to **Settings > Tokens** to create a new authentication token. Make sure to copy the full token value.",
            },
          ],
        },
        {
          title: "Enter your credentials",
          items: [
            {
              kind: "baseUrl",
              label: "Splunk base URL",
              required: true,
              placeholder: "https://yourinstance.splunkcloud.com:8089",
              target: { kind: "configuration", field: "baseUrl" },
              helperText:
                "The base URL you use to log in to Splunk Cloud or Enterprise, including https://. Include the management port (default is :8089).",
            },
            {
              kind: "input",
              label: "Authentication token",
              required: true,
              target: { kind: "secrets", field: "token" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: ({ baseUrl }) => `${baseUrl}/services/server/info`,
      },
      publicEnvVars,
    }),
    productVariant({
      identifier: "splunkBasicAuth",
      authLabel: "Username and password",
      keywords,
      protocol: "http",
      defaultUrlPattern: ({ baseUrl }) => {
        const pattern = exactUrlToRegex(baseUrl);
        return pattern
          ? `^${pattern.replace(/\/\.\*$/, "/services(?:/|NS/).*")}`
          : "";
      },
      auth: (app) =>
        basicAuth({
          username: app.config.username,
          password: app.secret.password,
        }),
      steps: [
        {
          title: "Get your Splunk credentials",
          items: [
            {
              kind: "markdown",
              content:
                "Use a Splunk account that supports [REST API username/password authentication](https://help.splunk.com/en/splunk-enterprise/leverage-rest-apis/rest-api-user-manual/9.4/rest-api-user-manual/basic-concepts-about-the-splunk-platform-rest-api#basic-http-authentication-0), with only the roles and capabilities required for your work. An SSO-only account is not sufficient. For Splunk Cloud, [enable REST API access](https://help.splunk.com/en/splunk-enterprise/leverage-rest-apis/rest-api-tutorials/10.4/rest-api-tutorials/access-requirements-and-limitations-for-the-splunk-cloud-platform-rest-api) and allow connections from 3B’s egress IP addresses.",
            },
          ],
        },
        {
          title: "Enter your credentials",
          items: [
            {
              kind: "baseUrl",
              label: "Splunk management base URL",
              required: true,
              placeholder: "https://yourinstance.splunkcloud.com:8089",
              target: { kind: "configuration", field: "baseUrl" },
              helperText:
                "Your Splunk Cloud or Enterprise HTTPS management base URL, including the port (default is :8089) and any installation path. Do not include /services/ or /servicesNS/. Credentials apply only to these management API paths, not separate HEC, Observability, or ACS endpoints.",
            },
            {
              kind: "input",
              label: "Username",
              required: true,
              target: { kind: "configuration", field: "username" },
            },
            {
              kind: "input",
              label: "Password",
              required: true,
              target: { kind: "secrets", field: "password" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: ({ baseUrl }) => ({
          url: `${(baseUrl || "").replace(/\/+$/, "")}/services/authentication/current-context?output_mode=xml`,
          method: "GET",
          expectedStatus: 200,
          expectedBodyContains: [
            "<title>context</title>",
            '<s:key name="username">',
          ],
        }),
      },
      publicEnvVars,
      usageHint: () =>
        "Use the configured management base URL with /services/ or /servicesNS/ API routes, including versioned search routes. Individual operations still require their own capabilities and resource permissions.",
    }),
  ],
});
